import AES from 'crypto-js/aes'
import EncUtf8 from 'crypto-js/enc-utf8'

import {
  WidgetModes,
  defaultUserlikeGroup,
  oldDataTimeoutInHours,
} from 'consts'
import isSessionStorageExists from 'utils/isSessionStorageExists'
import { AvailableCampaigns } from 'entities/Config'
import getWidgetMode from 'utils/getWidgetMode'
import * as Sentry from '@sentry/react'
import getLanguage from 'locales/getLanguage'
import CustomStorage from 'utils/customStorage'
import { storage as lStorage } from 'utils/localStorage'
import type { ConversationFlow } from 'entities/Message'
import type Message from 'entities/Message'
import type ContextOfConversation from 'entities/ContextOfConversation'
import type { ChatBackend } from 'entities/CommunicationService'

export enum SessionStorageKey {
  SessionStorageMessagesKey = 'sae-conversational-messages',
  SessionStorageMessagesBufferKey = 'sae-conversational-msg-buffer',
  SessionStorageContextsKey = 'sae-conversational-contexts',
  SessionStorageLastUserKey = 'sae-conversational-last-user',
  SessionStorageUserlike = 'sae-conversational-userlike',
  SessionStorageUserlikeGroup = 'sae-conversational-userlike-group',
  SessionStorageSettings = 'sae-conversational-settings',
  SessionStorageLanguage = 'sae-conversational-language',
  SessionStorageConsentMessageClosed = 'sae-conversational-consent-msg-closed',
  SessionStorageTriggerIntent = 'sae-convesational-trigger-intent',
  SessionStorageUnreadMessages = 'sae-conversational-unread-messages',
  SessionStorageFullStorySessionStarted = 'sae-conversational-fullstory-session-started',
  SessionStorageConversationFlow = 'sae-conversational-conversation-flow',
  CrossSellWidgetData = 'crosssellwidgetdata',
}

const widgetMode = getWidgetMode()
const isEmbeddedInWebview = widgetMode === WidgetModes.MobileApp

type AllStorageObject = Record<SessionStorageKey, string>
export const localStoragePrefix = 'sae-session:'

const getStorageType = () => {
  if (isEmbeddedInWebview) {
    return lStorage
  }

  return sessionStorage
}

export const storage = isSessionStorageExists()
  ? getStorageType()
  : new CustomStorage()

export const removeItem = (key: SessionStorageKey) => {
  storage.removeItem(key)

  if (!isEmbeddedInWebview) {
    lStorage.removeItem(`${localStoragePrefix}${key}`)
  }
}

export const clearSessionStorage = () => {
  removeItem(SessionStorageKey.SessionStorageMessagesKey)
  removeItem(SessionStorageKey.SessionStorageMessagesBufferKey)
  removeItem(SessionStorageKey.SessionStorageContextsKey)
  removeItem(SessionStorageKey.SessionStorageLastUserKey)
  removeItem(SessionStorageKey.SessionStorageUserlike)
  removeItem(SessionStorageKey.SessionStorageUserlikeGroup)
  removeItem(SessionStorageKey.SessionStorageFullStorySessionStarted)
}

export const getAllSessionStorage = (): AllStorageObject => {
  const allStorage: AllStorageObject = Object.values(SessionStorageKey).reduce(
    (acc, key) => {
      const stateObject = acc
      const storageKey = key as SessionStorageKey
      const value = storage.getItem(storageKey)

      if (value) {
        stateObject[storageKey] = value
      }
      return stateObject
    },
    {} as AllStorageObject
  )

  return allStorage
}

const loadItem = <Value = any>(
  key: SessionStorageKey,
  defaultValue: any,
  decrypt?: boolean
): Value => {
  try {
    const serialisedValue = storage.getItem(key)
    if (serialisedValue === null || typeof serialisedValue === 'undefined') {
      return defaultValue
    }

    if (decrypt && process.env.REACT_APP_KITTEN_TUPEL) {
      const bytes = AES.decrypt(
        serialisedValue,
        process.env.REACT_APP_KITTEN_TUPEL
      )

      return JSON.parse(bytes.toString(EncUtf8))
    }

    return JSON.parse(serialisedValue)
  } catch (error) {
    if (error instanceof SyntaxError) {
      // eslint-disable-next-line no-console
      console.error(
        `Local storage contains invalid JSON under ${key}. Clearing whole sessionStorage`
      )
      clearSessionStorage()
    } else {
      // eslint-disable-next-line no-console
      console.error(
        `Failed to load ${key} from local storage: ${(error as any).message}`
      )
    }
    return defaultValue
  }
}

export const saveItem = <Value = any>(
  key: SessionStorageKey,
  value: Value,
  encrypt?: boolean
): void => {
  try {
    let serialisedValue = JSON.stringify(value)

    if (encrypt && process.env.REACT_APP_KITTEN_TUPEL) {
      serialisedValue = AES.encrypt(
        serialisedValue,
        process.env.REACT_APP_KITTEN_TUPEL
      ).toString()
    }

    storage.setItem(key, serialisedValue)

    if (!isEmbeddedInWebview) {
      lStorage.setItem(`${localStoragePrefix}${key}`, serialisedValue)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to save ${key} to local storage: ${(error as any).message}`
    )
  }
}

export const loadMessagesFromSessionStorage = (
  key = SessionStorageKey.SessionStorageMessagesKey
): Array<Message> => {
  const messages: Array<Message> = loadItem(key, [], true)
  if (!Array.isArray(messages) || messages.length === 0) {
    return []
  }

  const nowDate = new Date().getTime()
  const lastMessageDate = new Date(
    messages[messages.length - 1].timestamp
  ).getTime()

  const hoursInMilliseconds = 1000 * 60 * 60 * oldDataTimeoutInHours

  if (nowDate - lastMessageDate > hoursInMilliseconds) {
    clearSessionStorage()
    return []
  }

  return messages.map((item: Message) => ({
    ...item,
    timestamp: new Date(item.timestamp),
  }))
}

export const saveMessagesToSessionStorage = (
  responses: Array<Message>,
  key: SessionStorageKey = SessionStorageKey.SessionStorageMessagesKey
): void => {
  const filtredResponses = responses.filter((el) => !el.messageObject.protected)
  saveItem(
    key,
    filtredResponses.map((el) => ({
      ...el,
      messageObject: {
        ...el.messageObject,
        delayBeforeAppear: undefined,
        delayBeforeAppend: undefined,
      },
    })),
    true
  )
}

export const saveBufferMessagesToSessionStorage = (
  responses: Array<Message>
): void => {
  saveMessagesToSessionStorage(
    responses,
    SessionStorageKey.SessionStorageMessagesBufferKey
  )
}

export const removeLastBufferMessageItemFromSessionStorage = () => {
  const bufferMessages = loadItem(
    SessionStorageKey.SessionStorageMessagesBufferKey,
    [],
    true
  )

  const updatedMessages = bufferMessages.splice(1)

  saveMessagesToSessionStorage(
    updatedMessages,
    SessionStorageKey.SessionStorageMessagesBufferKey
  )
}

export const clearMessageBufferFromSessionStorage = () => {
  removeItem(SessionStorageKey.SessionStorageMessagesBufferKey)
}

export const mergeBufferMessagesInSessionStorage = () => {
  const bufferMessages = loadItem(
    SessionStorageKey.SessionStorageMessagesBufferKey,
    [],
    true
  )

  if (bufferMessages.length) {
    const messages = loadItem(
      SessionStorageKey.SessionStorageMessagesKey,
      [],
      true
    )
    const mergedMessages = [...messages, ...bufferMessages]

    saveMessagesToSessionStorage(mergedMessages)
  }

  clearMessageBufferFromSessionStorage()
}

export const loadMessageBufferFromSessionStorage = () =>
  loadMessagesFromSessionStorage(
    SessionStorageKey.SessionStorageMessagesBufferKey
  )

export const loadContextsFromSessionStorage =
  (): Array<ContextOfConversation> =>
    loadItem(SessionStorageKey.SessionStorageContextsKey, [], true)

export const saveContextsToSessionStorage = (
  contexts: Array<ContextOfConversation>
): void => saveItem(SessionStorageKey.SessionStorageContextsKey, contexts, true)

export const saveStateToSessionStorage = (
  messages: Array<Message>,
  contexts: Array<ContextOfConversation>
) => {
  saveContextsToSessionStorage(contexts)
  saveMessagesToSessionStorage(messages)
}

export const loadStateFromSessionStorage = () => ({
  contexts: loadContextsFromSessionStorage(),
  messages: loadMessagesFromSessionStorage(),
})

export const setUserlikeSession = (value: boolean) => {
  if (value) {
    saveItem(SessionStorageKey.SessionStorageUserlike, 'true')
  } else {
    removeItem(SessionStorageKey.SessionStorageUserlike)
  }

  Sentry.addBreadcrumb({
    category: 'user-action',
    message: 'Userlike session set',
    level: 'info',
    data: {
      value,
    },
  })
}

export const setUserlikeGroup = (group: string) => {
  saveItem(SessionStorageKey.SessionStorageUserlikeGroup, group)
}

export const getUserlikeGroup = () =>
  loadItem(SessionStorageKey.SessionStorageUserlikeGroup, defaultUserlikeGroup)

export const removeUserlikeGroup = () => {
  removeItem(SessionStorageKey.SessionStorageUserlikeGroup)
}

export const getUserlikeSession = () =>
  loadItem(SessionStorageKey.SessionStorageUserlike, null)

export const encryptValue = (key: SessionStorageKey) => {
  let serialisedValue = storage.getItem(key)

  if (serialisedValue === null || typeof serialisedValue === 'undefined') {
    return
  }

  try {
    JSON.parse(serialisedValue)

    if (process.env.REACT_APP_KITTEN_TUPEL) {
      serialisedValue = AES.encrypt(
        serialisedValue,
        process.env.REACT_APP_KITTEN_TUPEL
      ).toString()

      storage.setItem(key, serialisedValue)
    }
  } catch (_) {} // eslint-disable-line
}

export const encryptCurrentValues = () => {
  encryptValue(SessionStorageKey.SessionStorageMessagesKey)
  encryptValue(SessionStorageKey.SessionStorageContextsKey)
}

export const getBackendSession = () => {
  const { backend } = loadItem(SessionStorageKey.SessionStorageSettings, {
    backend: 'backend-1',
  })

  return backend
}

export const setBackendSession = (backend: ChatBackend) => {
  const settings = loadItem(SessionStorageKey.SessionStorageSettings, {})
  saveItem(SessionStorageKey.SessionStorageSettings, {
    ...settings,
    backend,
  })
}

export const isBackend = (backends: Array<ChatBackend>) =>
  backends.includes(getBackendSession())

export const loadSettingsItems = () =>
  loadItem(SessionStorageKey.SessionStorageSettings, {})

export const saveSettingsItems = (properties: object) => {
  const settings = loadItem(SessionStorageKey.SessionStorageSettings, {})
  saveItem(SessionStorageKey.SessionStorageSettings, {
    ...settings,
    ...properties,
  })
}

export const clearSettingsItems = (key: string) => {
  const settings = loadItem(SessionStorageKey.SessionStorageSettings, {})
  delete settings[key]
  saveItem(SessionStorageKey.SessionStorageSettings, settings)
}

export const setLanguageInSessionStorage = (language: string) => {
  saveItem(SessionStorageKey.SessionStorageLanguage, language)
}

export const getLanguageInSessionStorage = () => {
  const defaultLanguage = getLanguage()

  return loadItem(SessionStorageKey.SessionStorageLanguage, defaultLanguage)
}

export const getConsultId = (themeId: string) => {
  const settingsItems = loadSettingsItems()
  return settingsItems?.[`campaign-${themeId}`]?.consultID
}

export const setLastScrollPoint = (top: number, themeId: string) => {
  const settingsItems = loadSettingsItems()
  if (themeId === AvailableCampaigns.default) {
    settingsItems.lastScrollPoint = top
  } else if (`campaign-${themeId}` in settingsItems) {
    settingsItems[`campaign-${themeId}`].lastScrollPoint = top
  }

  saveItem(SessionStorageKey.SessionStorageSettings, settingsItems)
}

export const getLastScrollPoint = (themeId: string) => {
  const settingsItems = loadSettingsItems()
  if (settingsItems?.[`campaign-${themeId}`]) {
    return settingsItems?.[`campaign-${themeId}`]?.lastScrollPoint
  }

  return settingsItems?.lastScrollPoint
}

export const clearScrollPoint = (themeId: string) => {
  const settingsItems = loadSettingsItems()
  if (settingsItems?.[`campaign-${themeId}`]) {
    delete settingsItems?.[`campaign-${themeId}`]?.lastScrollPoint
  } else {
    delete settingsItems?.lastScrollPoint
  }

  saveItem(SessionStorageKey.SessionStorageSettings, settingsItems)
}

export const getActiveCampaignFromStorage = () => {
  const settingsItems = loadSettingsItems()
  const activeCampaign = settingsItems?.activeCampaign

  const campaign = activeCampaign && settingsItems?.[activeCampaign]

  if (campaign) {
    campaign.id = activeCampaign.slice('campaign-'.length)
  }

  return campaign
}

export const getActiveIdFromStorage = () => {
  const settingsItems = loadSettingsItems()
  const activeCampaign = settingsItems?.activeCampaign

  const campaign = activeCampaign && settingsItems?.[activeCampaign]

  if (campaign) {
    return `${campaign.consultID}-${campaign.consultIteration}`
  }

  return undefined
}

export const setWelcomeMessageClosed = (themeId: string) => {
  const settingsItems = loadSettingsItems()
  const key = `campaign-${themeId}`

  if (!settingsItems?.[key]) {
    settingsItems[key] = {}
  }

  settingsItems[key].welcomeMessageClosed = true
  saveItem(SessionStorageKey.SessionStorageSettings, settingsItems)
}

export const getWelcomeMessageClosed = (themeId: string): boolean => {
  const settingsItems = loadSettingsItems()
  const key = `campaign-${themeId}`

  return Boolean(settingsItems?.[key]?.welcomeMessageClosed)
}

export const setConsentMessageClosed = () => {
  saveItem<boolean>(SessionStorageKey.SessionStorageConsentMessageClosed, true)
}

export const getConsentMessageClosed = (): boolean =>
  JSON.parse(
    loadItem(SessionStorageKey.SessionStorageConsentMessageClosed, false)
  )

export const clearConsentMessageClosed = () => {
  removeItem(SessionStorageKey.SessionStorageConsentMessageClosed)
}

export interface LaunchIntentData {
  intent: string
  theme: AvailableCampaigns
}

export const setLaunchIntentData = (intent: string) => {
  try {
    const intentJson = JSON.parse(intent) as LaunchIntentData
    // Validate data
    if (!['intent', 'theme'].every((prop) => prop in intentJson)) {
      throw new Error('Launch intent data missing required property')
    }
    saveItem<string>(SessionStorageKey.SessionStorageTriggerIntent, intent)
  } catch (err) {
    const error = err as Error

    // eslint-disable-next-line
    console.error(`Unable to launch chat from button: Reason: ${error.message}`)
  }
}

export const getLaunchIntentData = (): LaunchIntentData | null => {
  const intentData = loadItem<string | null>(
    SessionStorageKey.SessionStorageTriggerIntent,
    null
  )

  return intentData ? JSON.parse(intentData) : null
}

export const clearLaunchIntentData = () => {
  removeItem(SessionStorageKey.SessionStorageTriggerIntent)
}

export const setUnreadMessagesInSessionStorage = (unread: boolean) => {
  if (unread) {
    saveItem<boolean>(SessionStorageKey.SessionStorageUnreadMessages, unread)
  } else {
    removeItem(SessionStorageKey.SessionStorageUnreadMessages)
  }
}

export const getUnreadMessagesFromSessionStorage = (): boolean =>
  Boolean(loadItem(SessionStorageKey.SessionStorageUnreadMessages, false))

export const setIsRasaHandoffActive = (value: boolean) => {
  saveSettingsItems({
    isRasaHandoffActive: value,
  })
}

export const getIsRasaHandoffActive = (): boolean =>
  Boolean(loadSettingsItems()?.isRasaHandoffActive)

export const clearSessionStorageAndResetBackend = (backend: ChatBackend) => {
  clearSessionStorage()
  setBackendSession(backend)
}

export const getFullStorySessionStarted = (): boolean =>
  Boolean(
    loadItem(SessionStorageKey.SessionStorageFullStorySessionStarted, false)
  )

export const setFullStorySessionStarted = (started: boolean): void => {
  saveItem(SessionStorageKey.SessionStorageFullStorySessionStarted, started)
}

export const setConversationFlow = (flow: ConversationFlow) => {
  saveItem(SessionStorageKey.SessionStorageConversationFlow, flow)
}

export const getConversationFlow = (): ConversationFlow | null =>
  loadItem(SessionStorageKey.SessionStorageConversationFlow, null)

export const clearConversationFlow = () => {
  removeItem(SessionStorageKey.SessionStorageConversationFlow)
}
